import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import Dialog from "@mui/material/Dialog";

import { trackPlg } from "plg/analytics/events";
import { BrowseArt, MeetArt, RequestArt, ShortlistArt } from "plg/screens/Home/TourArt";
import type { TourPeople } from "plg/screens/Home/TourArt";
import "plg/screens/Home/Tour.scss";

interface Step {
  title: string;
  text: string;
  art: (people: TourPeople) => ReactNode;
}

// Prototype TOUR (home-tour.html): four steps, each a graphic over its copy. The first three are the customer's,
// the last is ours.
const STEPS: Step[] = [
  {
    title: "Browse",
    text: "Real profiles, already vetted and trained for US practices. No call and nothing to sign.",
    art: (people) => <BrowseArt people={people} />,
  },
  {
    title: "Shortlist",
    text: "Save anyone you would want to meet. Your shortlist tells us what good looks like to you.",
    art: (people) => <ShortlistArt people={people} />,
  },
  {
    title: "Request a meeting",
    text: "Tell us what you need and when suits you. It takes about a minute.",
    art: () => <RequestArt />,
  },
  {
    title: "Meet with a hiring expert",
    text: "They pick it up with your shortlist already in hand, so the call starts somewhere useful.",
    art: (people) => <MeetArt people={people} />,
  },
];

interface Props {
  /** The home page's preview talent, shown in the graphics. */
  people: TourPeople;
  onClose: () => void;
  /** The last step's Browse talent. */
  onBrowse: () => void;
}

/**
 * "What happens next?" on Home (prototype renderTour): four modals, Next and Back between them; Close on the first and
 * the last, Browse talent to finish.
 */
function WhatHappensNextTour({ people, onClose, onBrowse }: Props) {
  const [at, setAt] = useState(0);
  const step = STEPS[at];
  const last = at === STEPS.length - 1;
  const completed = useRef(false);

  useEffect(() => {
    if (last && !completed.current) {
      completed.current = true;
      trackPlg("walkthrough_completed", {});
    }
  }, [last]);

  // Leaving before the last step is a skip (which step they left on).
  const close = () => {
    if (!completed.current) trackPlg("walkthrough_skipped", { step: at + 1 });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={close}
      maxWidth={false}
      slotProps={{
        paper: { className: "tour-pop", "aria-label": step.title } as object,
        backdrop: { sx: { backgroundColor: "rgba(42, 18, 65, 0.32)" } },
      }}
    >
      {step.art(people)}
      <div className="tour-copy" key={at}>
        <div className="tour-eyebrow">
          Step {at + 1} of {STEPS.length}
        </div>
        <h2>{step.title}</h2>
        <p>{step.text}</p>
      </div>
      <div className="tour-foot">
        {at === 0 || last ? (
          <button type="button" className="plg-btn plg-btn--outline tour-btn" onClick={close}>
            Close
          </button>
        ) : (
          <button type="button" className="plg-btn plg-btn--outline tour-btn" onClick={() => setAt(at - 1)}>
            Back
          </button>
        )}
        {last ? (
          <button type="button" className="plg-btn plg-btn--primary tour-btn" onClick={onBrowse}>
            Browse talent
          </button>
        ) : (
          <button type="button" className="plg-btn plg-btn--primary tour-btn" onClick={() => setAt(at + 1)}>
            Next
          </button>
        )}
      </div>
    </Dialog>
  );
}

export default WhatHappensNextTour;
